import * as React from "react";
import type { LucideIcon } from "lucide-react";

import { cn } from "../lib/cn";

/**
 * PlatformAreaLayout — DRY full-screen modal-area shell used by every
 * "platform area" surface (Admin, Settings, Profile, future Org Console).
 *
 * v9 (Jun 13, 2026) — "no background at all"
 * ------------------------------------------
 * v8 still carried a ``bg-black/[0.05]`` tint on active + hover (same
 * MenuItem treatment as the avatar drawer). User pushed further:
 * "NON deve avere uno sfondo le voci del menu". Strip all backgrounds.
 * v9 is pure typography — the Linear / Notion / Vercel / Stripe 2026
 * settings-rail consensus.
 *
 * Stack note: there is no Radix/shadcn primitive purpose-built for a
 * vertical settings rail. ``@radix-ui/react-navigation-menu`` is for
 * horizontal menubars with hover popouts (wrong tool). shadcn's
 * ``Sidebar`` adds a context provider + collapse/mobile-drawer state we
 * don't need. The semantic-HTML ``<nav>`` + ``<a aria-current="page">``
 * pattern IS what Linear/Notion/Vercel ship; the avatar ``MenuItem`` is
 * also just a styled ``<button role="menuitem">``. So we keep this
 * primitive and lean on the same ``cn()`` + Tailwind token stack.
 *
 *   - ``h-9`` + ``px-2.5`` + no rounded chip
 *   - ``font-medium text-[13px]`` default in ``text-ink-2``
 *   - hover → ``text-ink-1`` (NO bg)
 *   - active → ``bg-ando-orange/10`` + ``font-semibold text-ink-1`` + icon
 *     ``text-ando-orange`` (Sep 7, 2026: the tint came back because the
 *     typography-only row did not read as selected). No left bar.
 *
 * Rail title: typography-only (``font-semibold`` + ``text-ink-1``), no
 * brand chip — Granola/Notion/Linear all converged on this in 2026.
 *
 * The IA from v3-v6 is preserved: 208px width, eyebrow buckets opt-in,
 * arrow-key roving focus, hairline separators when eyebrows are off.
 *
 * Visual hierarchy (top to bottom of rail):
 *
 *   ┌─────────────────────┐
 *   │  Impostazioni       │  ← rail title (font-display, text only)
 *   ├─────────────────────┤
 *   │  ACCOUNT            │  ← eyebrow (opt-in)
 *   │  ◉ Profilo (active) │  ← bold + orange icon, no chrome
 *   │  □ Sicurezza        │
 *   │  □ Privacy & dati   │
 *   └─────────────────────┘
 *
 * Architecture note — why router DI instead of importing TanStack:
 *   ``@ando/ui`` deliberately does NOT depend on ``@tanstack/react-router``
 *   so the package stays usable from the Next.js (``apps/web``) build, the
 *   Storybook target, and any future Tauri-only surface. The two thin
 *   primitives the layout needs (a Link component + an "is this path
 *   active" predicate) are injected by the caller via the
 *   ``PlatformAreaRouterAdapter`` prop. The app passes a small wrapper
 *   around TanStack's ``Link`` + ``useMatchRoute`` (~10 LoC) — see
 *   ``apps/client/src/features/platform-area/router-adapter.tsx``.
 */

/**
 * Single navigation entry.
 *
 * ``to`` is typed as ``string`` because the router-typed literal union
 * belongs at the call site (only the app knows its routes). Each caller
 * types its own ``NAV_GROUPS`` const + the adapter funnels through the
 * typed ``Link``.
 */
export interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  /**
   * When ``false``, the item is filtered out of the rail. Useful for
   * capability-gated rows (e.g. admin-only entries inside a shared shell).
   * Default ``true`` (shown).
   */
  visible?: boolean;
}

/**
 * A logical group of nav items.
 *
 * v3 (May 24, 2026) — eyebrow is now intentionally **rendered as nothing**.
 * Groups are still useful semantically (to bucket items in the source) and
 * become a thin hairline separator between buckets in the rendered rail.
 * We keep the ``eyebrow`` field optional + readable so callers that DO
 * want a labelled bucket (rare — future "Profile" section perhaps) can
 * opt in via the new ``showGroupEyebrows`` prop on the layout.
 *
 * Groups whose items are ALL hidden (after the ``visible`` filter)
 * collapse to nothing — no empty separator stays behind.
 */
export interface NavGroup {
  eyebrow?: string;
  items: readonly NavItem[];
}

/**
 * Router adapter — small DI surface that the caller wires up to its own
 * routing library. Keeps ``@ando/ui`` framework-agnostic.
 *
 *  - ``Link`` renders an anchor that navigates to ``props.to`` when
 *    clicked. The caller forwards the click to its router.
 *  - ``useIsActive`` returns ``true`` iff the current location exactly
 *    matches ``to`` (no fuzzy / partial matches — ``/settings`` must NOT
 *    light up while on ``/settings/security``).
 */
export interface PlatformAreaRouterAdapter {
  Link: React.ComponentType<{
    to: string;
    className?: string;
    "aria-current"?: "page" | undefined;
    children: React.ReactNode;
  }>;
  useIsActive: (to: string) => boolean;
}

export interface PlatformAreaLayoutProps {
  /**
   * Semantic name of this surface (e.g. "Admin", "Impostazioni"). v7
   * RENDERS this as the rail title block (Ando glyph chip +
   * ``font-display``) — restores area identity inside the modal shell
   * since the parent chrome strip only carries the back affordance.
   * Also used as the ``<nav>``'s ``aria-label`` and for analytics tagging.
   */
  title: string;
  /** Declarative nav definition — caller owns the routes. */
  groups: readonly NavGroup[];
  /** Routing primitives — see ``PlatformAreaRouterAdapter`` JSDoc. */
  router: PlatformAreaRouterAdapter;
  /** Page content (typically a TanStack ``<Outlet />``). */
  children: React.ReactNode;
  /**
   * Optional accessible label override. Defaults to ``Sezioni {title}``.
   */
  navAriaLabel?: string;
  /**
   * Opt-in escape hatch — render the (uppercase) eyebrow label above
   * each group. Default ``false`` (v3 flat-list aesthetic). Future
   * surfaces with truly heterogeneous categories (e.g. "Account",
   * "Danger zone") can flip this on per-instance.
   */
  showGroupEyebrows?: boolean;
}

/**
 * Width of the left rail. **208px** (``w-52``) — sweet spot per the
 * May 2026 SaaS audit:
 *   - Linear: 220px (slightly wider, includes badges)
 *   - Vercel: 200px (no badges, denser type)
 *   - Cursor: 184px (mono-font, very dense)
 *   - Notion: 240px (icons + emoji + handle)
 * 208 fits all our current labels (longest = "Template presentazioni",
 * 22 chars in Manrope 12.5px → ~158px) with a 20px right gutter for the
 * scroll thumb + 16px breathing room. Reclaims 32px vs the v2 240px rail.
 */
const RAIL_WIDTH_CLASS = "w-52";

export function PlatformAreaLayout({
  title,
  groups,
  router,
  children,
  navAriaLabel,
  // v4 (May 31, 2026) — eyebrow ON by default. Previous flat-list
  // aesthetic relied on hairline separators that were too quiet to
  // bucket the IA visually; users couldn't tell Account / Workspace /
  // Esperienza apart at a glance. Linear's May 2026 settings rebuild
  // also reintroduced eyebrows. Callers can still opt out per-instance.
  showGroupEyebrows = true,
}: PlatformAreaLayoutProps): React.ReactElement {
  // Pre-filter groups: drop hidden items, then drop empty groups so the
  // separator hairline doesn't render alone (e.g. non-admin user landing
  // on Settings would otherwise see a leading divider with no content).
  const visibleGroups = React.useMemo<readonly NavGroup[]>(
    () =>
      groups
        .map((group) => ({
          eyebrow: group.eyebrow,
          items: group.items.filter((item) => item.visible !== false),
        }))
        .filter((group) => group.items.length > 0),
    [groups],
  );

  // Flat list of refs for roving arrow-key focus. We rebuild it whenever
  // visibleGroups changes — cheap (≤15 items) and avoids stale closures.
  const itemRefs = React.useRef<(HTMLAnchorElement | null)[]>([]);
  const totalItems = visibleGroups.reduce(
    (sum, g) => sum + g.items.length,
    0,
  );
  // Trim the ref array on shape change so old (removed) rows don't leak.
  if (itemRefs.current.length !== totalItems) {
    itemRefs.current = Array.from({ length: totalItems }, (_, i) =>
      itemRefs.current[i] ?? null,
    );
  }

  const onRailKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLElement>): void => {
      const focusables = itemRefs.current.filter(
        (el): el is HTMLAnchorElement => el != null,
      );
      if (focusables.length === 0) return;

      const currentIdx = focusables.findIndex(
        (el) => el === document.activeElement,
      );
      let nextIdx: number | null = null;
      switch (event.key) {
        case "ArrowDown":
          nextIdx =
            currentIdx === -1 ? 0 : (currentIdx + 1) % focusables.length;
          break;
        case "ArrowUp":
          nextIdx =
            currentIdx === -1
              ? focusables.length - 1
              : (currentIdx - 1 + focusables.length) % focusables.length;
          break;
        case "Home":
          nextIdx = 0;
          break;
        case "End":
          nextIdx = focusables.length - 1;
          break;
        default:
          return;
      }
      if (nextIdx == null) return;
      event.preventDefault();
      focusables[nextIdx]?.focus();
    },
    [],
  );

  // Pre-compute the flat index of each item (group-major order) so the
  // NavItemLink can register itself in `itemRefs` at a stable slot.
  let runningIdx = 0;

  return (
    <div className="flex h-full w-full">
      <nav
        aria-label={navAriaLabel ?? `Sezioni ${title}`}
        onKeyDown={onRailKeyDown}
        className={cn(
          "flex h-full shrink-0 flex-col overflow-y-auto",
          "border-hairline border-r bg-cream/40",
          // Tight vertical padding — chrome strip already gives 12px top
          // headroom. Bottom padding lets the last row breathe.
          "px-2 pt-3 pb-4",
          RAIL_WIDTH_CLASS,
        )}
      >
        {/* Rail title — typography-only, no chip. Anchors orientation
            ("Impostazioni" vs "Admin") without breaking the pencil-clean
            aesthetic. Granola / Notion / Linear all converged on this in
            2026: ``font-display`` for the personality, ink-1 for weight,
            no surface chrome competing with the nav rows underneath. */}
        <div
          className="mb-3 px-2.5 pt-1 font-display font-semibold text-[14px] text-ink-1 tracking-[-0.012em]"
          aria-hidden
        >
          {title}
        </div>

        {visibleGroups.map((group, groupIdx) => {
          const isLast = groupIdx === visibleGroups.length - 1;
          const groupStartIdx = runningIdx;
          runningIdx += group.items.length;
          // Separator strategy:
          //   - Eyebrows ON  → the uppercase label is the visible divider;
          //                    just add breathing space between groups.
          //   - Eyebrows OFF → render a hairline between buckets
          //                    (flat-list aesthetic, no labels).
          const separator = isLast ? null : showGroupEyebrows ? (
            <div className="h-2" aria-hidden />
          ) : (
            <div
              role="separator"
              aria-orientation="horizontal"
              className="mx-2 my-2 border-hairline border-t"
            />
          );
          return (
            <React.Fragment key={group.eyebrow ?? `group-${groupIdx}`}>
              <NavGroupBlock
                group={group}
                router={router}
                showEyebrow={showGroupEyebrows}
                startIdx={groupStartIdx}
                refs={itemRefs}
              />
              {separator}
            </React.Fragment>
          );
        })}
      </nav>
      <main className="min-w-0 flex-1 overflow-hidden">{children}</main>
    </div>
  );
}

interface NavGroupBlockProps {
  group: NavGroup;
  router: PlatformAreaRouterAdapter;
  showEyebrow: boolean;
  startIdx: number;
  refs: React.MutableRefObject<(HTMLAnchorElement | null)[]>;
}

function NavGroupBlock({
  group,
  router,
  showEyebrow,
  startIdx,
  refs,
}: NavGroupBlockProps): React.ReactElement {
  return (
    <div className="flex flex-col gap-0.5">
      {showEyebrow && group.eyebrow ? (
        <div
          className={cn(
            "px-2.5 pt-2 pb-1.5 font-semibold text-[10px] text-ink-3/80 uppercase tracking-[0.12em]",
          )}
        >
          {group.eyebrow}
        </div>
      ) : null}
      {group.items.map((item, idx) => (
        <NavItemLink
          key={item.to}
          item={item}
          router={router}
          registerRef={(el) => {
            refs.current[startIdx + idx] = el;
          }}
        />
      ))}
    </div>
  );
}

interface NavItemLinkProps {
  item: NavItem;
  router: PlatformAreaRouterAdapter;
  registerRef: (el: HTMLAnchorElement | null) => void;
}

function NavItemLink({
  item,
  router,
  registerRef,
}: NavItemLinkProps): React.ReactElement {
  const { Link, useIsActive } = router;
  const Icon = item.icon;
  const active = useIsActive(item.to);

  // The Link adapter renders an <a>. We grab the underlying DOM node via
  // a callback ref on a wrapping span and walk to firstChild — but that's
  // brittle. Instead, the adapter forwards className + we attach the ref
  // through a render-prop-less trick: stamp the ref on the first <a>
  // we find inside this container at mount. Simpler: use a span wrapper
  // with cb ref + querySelector — but cleanest is to expose the ref via
  // the adapter contract. To avoid breaking the public adapter, we use a
  // wrapping div with the cb ref + ``firstElementChild``.
  const wrapperRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) {
        registerRef(null);
        return;
      }
      const anchor = node.querySelector<HTMLAnchorElement>("a");
      registerRef(anchor);
    },
    [registerRef],
  );

  return (
    <div ref={wrapperRef} className="contents">
      <Link
        to={item.to}
        aria-current={active ? "page" : undefined}
        className={cn(
          // ── Base — semantic <a> styled like the avatar MenuItem.
          //    Idle and hover rows stay background-free (Jun 13, 2026
          //    direction); the ACTIVE row gets the same soft orange tint
          //    used for selected rows elsewhere (Sep 7, 2026) — typography
          //    alone did not read as "selected".
          "relative flex h-9 items-center gap-2.5 px-2.5",
          "font-medium text-[13px] leading-[18px]",
          "transition-colors duration-150 ease-out",
          "rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/60 focus-visible:ring-offset-1",
          // ── Active vs idle ────────────────────────────────────────
          //    Idle ``text-ink-2``. Hover bumps to ``text-ink-1``
          //    (no bg). Active: soft tint + ``font-semibold text-ink-1``
          //    + orange icon.
          active
            ? "bg-ando-orange/10 font-semibold text-ink-1"
            : "text-ink-2 hover:text-ink-1",
        )}
      >
        <Icon
          size={15}
          strokeWidth={1.75}
          aria-hidden
          className={cn(
            "shrink-0 transition-colors duration-150",
            active ? "text-ando-orange" : "text-ink-3",
          )}
        />
        <span className="truncate">{item.label}</span>
      </Link>
    </div>
  );
}

PlatformAreaLayout.displayName = "PlatformAreaLayout";
